import type { CustomDimensions } from '@de/types/types';
import { InterpolationError } from '@de/utils/interpolation';
import {
  parseCustomDimensionMapping,
  shouldSlugifyCustomDimensionValue,
  type CustomDimensionMapping,
  type ProductFieldMapping,
} from './productEventMappingContract';
import {
  resolveProductFields,
  resolveMappingValue,
  slugifyProductValue,
} from './resolveProductFields';

/**
 * Inputs for {@link applyProductContext}. Callers pass Entry
 * `flowContext` or EL repeater `item` as `root` — this helper does
 * not look those names up.
 */
export type ApplyProductContextArgs = {
  /**
   * Dual-shape `tracking.productFields` map. Missing or empty means
   * return {@link ApplyProductContextArgs.baseProduct} unchanged.
   */
  readonly map?: Readonly<Record<string, ProductFieldMapping>>;
  /**
   * Resolve root for `{ dataPath }` entries (`flowContext` or `item`).
   */
  readonly root?: Readonly<Record<string, unknown>>;
  /**
   * Existing product override map to overlay onto. Unconfigured keys
   * stay as they were. `undefined` when there is no prior product map.
   */
  readonly baseProduct?: Readonly<Record<string, string>>;
};

/**
 * Overlay {@link resolveProductFields} onto a base product map so
 * Product identity is not reassembled in every emitter.
 *
 *   - No map / empty map → `baseProduct` unchanged (same reference).
 *   - Configured keys overlay the base; missing paths are omitted so
 *     today's defaults stay.
 *   - Empty resolve with no base → `undefined` so callers can omit
 *     `product` on the trigger (Entry click parity).
 *
 * Does not look up `flowContext` or `item`. Does not own Product
 * on/off — PE-4 `_meta.tracking.enabled: false` already skips the
 * Product lifecycle before this helper is called.
 *
 * @param {ApplyProductContextArgs} args - Map, resolve root, and base product
 * @returns {Record<string, string> | undefined} Merged product map
 */
export function applyProductContext({
  map,
  root,
  baseProduct,
}: ApplyProductContextArgs): Record<string, string> | undefined {
  const hasMap = map != null && Object.keys(map).length > 0;
  if (!hasMap) {
    return baseProduct as Record<string, string> | undefined;
  }

  const resolved = resolveProductFields(map, root);
  if (!baseProduct) {
    return Object.keys(resolved).length > 0 ? resolved : undefined;
  }
  if (Object.keys(resolved).length === 0) {
    return baseProduct as Record<string, string>;
  }
  return { ...baseProduct, ...resolved };
}

/**
 * Resolve `tracking.customDimensions` mappings against a caller-supplied
 * root (`flowContext` on Entry, repeater `item` on EL).
 *
 * Missing / invalid / empty values are omitted so platform dims stay.
 * Values are slugified when {@link shouldSlugifyCustomDimensionValue}
 * is true (always, per the mapping contract).
 *
 * @param {readonly CustomDimensionMapping[] | undefined} mappings - Sibling tracking array
 * @param {Readonly<Record<string, unknown>> | undefined} root - Resolve root
 * @returns {CustomDimensions} Sparse resolved `{ key, value }` entries
 */
export function resolveConfiguredCustomDimensions(
  mappings: readonly CustomDimensionMapping[] | undefined,
  root: Readonly<Record<string, unknown>> | undefined,
): CustomDimensions {
  if (!mappings || mappings.length === 0) return [];

  const out: CustomDimensions = [];
  for (const raw of mappings) {
    const parsed = parseCustomDimensionMapping(raw);
    if (parsed.kind === 'empty') continue;
    if (parsed.kind === 'invalid') {
      // eslint-disable-next-line no-console
      console.warn(
        `[ConfigurableStep] tracking.customDimensions entry is invalid (${parsed.reason}); skipping.`,
      );
      continue;
    }

    let resolved: string | undefined;
    try {
      resolved = resolveMappingValue(parsed, root);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        `[ConfigurableStep] tracking.customDimensions["${parsed.key}"]: failed to resolve path "${
          parsed.kind === 'dataPath' ? parsed.dataPath : ''
        }" — ${err instanceof InterpolationError ? err.message : String(err)}`,
      );
      continue;
    }

    if (resolved === undefined) continue;

    const value = shouldSlugifyCustomDimensionValue()
      ? slugifyProductValue(resolved)
      : resolved;
    if (value.length === 0) continue;

    out.push({ key: parsed.key, value });
  }
  return out;
}

/**
 * Additive merge: start from platform dims, overlay configured dims.
 * The same key from config replaces that platform value. Unrelated
 * platform keys keep their values and order. New config keys append.
 *
 * No configured overlay (missing or empty) returns `platform`
 * unchanged — including the same reference — so Product payloads stay
 * bit-identical to today.
 *
 * @param {CustomDimensions | undefined} platform - Today's base dims
 * @param {CustomDimensions | undefined} configured - Resolved config overlay
 * @returns {CustomDimensions | undefined} Merged dims
 */
export function mergeCustomDimensions(
  platform: CustomDimensions | undefined,
  configured: CustomDimensions | undefined,
): CustomDimensions | undefined {
  if (!configured || configured.length === 0) {
    return platform;
  }

  const merged = new Map<string, { key: string; value: string }>();
  for (const dim of platform ?? []) {
    merged.set(dim.key, dim);
  }
  for (const dim of configured) {
    merged.set(dim.key, dim);
  }
  return Array.from(merged.values());
}
